//>>> Domain detection utility for owner/tenant API routing
/**
 * Detects the current domain type based on window.location
 * Owner domains: admin.freightezcrm.com, owner.freightezcrm.com, etc.
 * Tenant domains: *.freightezcrm.com (subdomains), custom domains
 * 
 * @returns {string} 'owner' | 'tenant'
 */
export const detectDomain = () => {
    if (typeof window === 'undefined') {
        // SSR fallback - default to tenant
        return 'tenant';
    }

    const hostname = window.location.hostname;

    // Local dev: use VITE_DEFAULT_PORTAL (owner | tenant), default tenant
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        const p = import.meta.env.VITE_DEFAULT_PORTAL;
        if (p === 'owner' || p === 'tenant') return p;
        return 'tenant';
    }
    
    // Check for owner/admin domains
    const ownerPatterns = [
        /^admin\./i,
        /^owner\./i,
        /^manage\./i,
    ];

    // Check if it's explicitly an owner domain
    const isOwner = ownerPatterns.some(pattern => pattern.test(hostname));
    
    // Check for explicit owner flag in localStorage (for development)
    const explicitDomain = localStorage.getItem('__domain_override');
    if (explicitDomain === 'owner' || explicitDomain === 'tenant') {
        return explicitDomain;
    }

    // If matches owner patterns, return owner
    if (isOwner) {
        return 'owner';
    }

    // Default to tenant (subdomain or custom domain)
    return 'tenant';
};

/**
 * Gets the current domain type
 * @returns {string} 'owner' | 'tenant'
 */
export const getDomain = () => {
    return detectDomain();
};

/**
 * Checks if current domain is owner
 * @returns {boolean}
 */
export const isOwnerDomain = () => {
    return detectDomain() === 'owner';
};

/**
 * Checks if current domain is tenant
 * @returns {boolean}
 */
export const isTenantDomain = () => {
    return detectDomain() === 'tenant';
};

/**
 * Sets domain override for development/testing
 * @param {string} domain - 'owner' | 'tenant'
 */
export const setDomainOverride = (domain) => {
    if (domain === 'owner' || domain === 'tenant') {
        localStorage.setItem('__domain_override', domain);
    } else {
        localStorage.removeItem('__domain_override');
    }
};

